import { useApolloClient } from "@apollo/client";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import GetAppTwoToneIcon from "@mui/icons-material/GetAppTwoTone";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { Tooltip } from "@mui/material";
import { BackdropOpen } from "App";
import {
  StyledIconButton,
  StyledToolbarSection,
} from "features/WorklistTools/shared/components/styles/styled-components";
import { UploadWorklist } from "features/WorklistTools/shared/components/UploadWorklist";
import { WarningDialog } from "features/WorklistTools/shared/components/WarningDialog";
import { WorklistToolWarnings } from "features/WorklistTools/shared/components/WorklistToolWarnings";
import { useWorklistToolStateProvider } from "features/WorklistTools/shared/state/StateProvider";
import {
  GetExistingMethodReservationQuery,
  GetPlatesInfoDocument,
  GetPlatesInfoQuery,
  GetPlatesInfoQueryVariables,
  useDeleteExistingMethodReservationMutation,
  useGetExistingMethodReservationLazyQuery,
} from "graphql/generated/schema-types";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import React, { useEffect, useState } from "react";
import { Alert, AlertType } from "shared-components/toast";
import { NucleofectionToolActions } from "../state";
import { NucleofectionInternalAction } from "../state/action";
import { useGenerateWorklistClick } from "../state/handlers";
import { useNucleofectionStateProvider } from "../state/StateProvider";
import {
  getNucleofectionOptionalWorklistWarnings,
  getNucleofectionRequiredWorklistWarnings,
} from "../warnings";
import {
  parseUploadedWorklist,
  UploadedWorklist,
} from "./toolbarhandlers.ts/HandleUploadWorklist";

export const GeneralOptions = () => {
  const dispatch = useAppReduxDispatch();
  const { worklistToolInternalState } = useWorklistToolStateProvider();
  const { nucleofectionInternalState } = useNucleofectionStateProvider();
  const [requiredWarnings, setRequiredWarnings] = useState([""]);
  const [optionalWarnings, setOptionalWarnings] = useState([""]);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [uploadWorklistOpen, setUploadWorklistOpen] = useState(false);
  const generateWorklist = useGenerateWorklistClick();
  const { nucleofectionDispatch } = useNucleofectionStateProvider();

  const nucleofectionToolInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present
  );

  const apolloClient = useApolloClient();

  const fetchNucPlateInfo = async (plateBarcode: string) => {
    const { data } = await apolloClient.query<
      GetPlatesInfoQuery,
      GetPlatesInfoQueryVariables
    >({
      query: GetPlatesInfoDocument,
      variables: {
        where: {
          plateBarcode: { eq: plateBarcode },
        },
      },
      fetchPolicy: "network-only",
    });
    return data;
  };

  const checkForWarnings = async () => {
    const requiredWarning = await getNucleofectionRequiredWorklistWarnings(
      nucleofectionToolInfo.compoundPlateInfo,
      nucleofectionToolInfo.sourcePlateInfo,
      nucleofectionToolInfo.nucPlateInfo,
      nucleofectionToolInfo.intPlateInfo,
      nucleofectionToolInfo.destPlateInfo,
      nucleofectionToolInfo.worklistValues,
      nucleofectionInternalState,
      worklistToolInternalState
    );
    const optionalWarning = await getNucleofectionOptionalWorklistWarnings(
      nucleofectionToolInfo.nucPlateInfo,
      nucleofectionToolInfo.worklistValues,
      nucleofectionInternalState.isNucPlateAlreadyReserved
    );
    setRequiredWarnings(requiredWarning);
    setOptionalWarnings(optionalWarning);
  };

  const hasActiveReservation = (res?: GetExistingMethodReservationQuery) =>
    (res?.existingMethodReservation?.length ?? 0) > 0;

  const [getExistingMethodReservation, { data: methodReservation }] =
    useGetExistingMethodReservationLazyQuery({
      fetchPolicy: "network-only",
      onCompleted(data) {
        const sourcePlateAlreadyReserved = hasActiveReservation(data);
        if (sourcePlateAlreadyReserved) {
          Alert({
            type: AlertType.WARNING,
            message:
              "Source plate has already been reserved. You must delete the reservation if you would like to generate a worklist with this plate.",
          });
        }
      },
    });

  const isSourcePlateAlreadyReserved = hasActiveReservation(methodReservation);

  useEffect(() => {
    getExistingMethodReservation({
      variables: {
        where: {
          isCompleted: { eq: false },
          methodReservationsPlateBarcodes: {
            some: {
              plateBarcode: {
                in: nucleofectionToolInfo.sourcePlateInfo.map(
                  (item) => item.plateBarcode
                ),
              },
            },
          },
        },
      },
    });
  }, [nucleofectionToolInfo.sourcePlateInfo]);

  const [uploadingWorklist, setUploadingWorklist] = useState(false);

  useEffect(() => {
    BackdropOpen({
      open: uploadingWorklist,
      header: "Uploading Worklist",
      subText: "Do not reload page",
      size: 60,
    });
  }, [uploadingWorklist]);

  const handleGenerateWorklistClick = () => {
    if (requiredWarnings.length > 0) {
      return;
    }
    const warnings = getNucleofectionOptionalWorklistWarnings(
      nucleofectionToolInfo.nucPlateInfo,
      nucleofectionToolInfo.worklistValues,
      nucleofectionInternalState.isNucPlateAlreadyReserved
    );
    setOptionalWarnings(warnings);
    if (warnings.length > 0) {
      setWarningDialogOpen(true);
    } else {
      generateWorklist();
    }
  };

  const [deleteMethodReservation] =
    useDeleteExistingMethodReservationMutation();

  const handleDeleteMethodReservationClick = () => {
    if (!methodReservation?.existingMethodReservation) {
      Alert({
        type: AlertType.ERROR,
        message:
          "Cannot find Method Reservation to delete. Reload page and try again.",
      });
      return;
    }

    deleteMethodReservation({
      variables: {
        methodReservationId:
          methodReservation!.existingMethodReservation[0]!.methodReservationId,
      },
    }).then((res) => {
      getExistingMethodReservation({
        variables: {
          where: {
            isCompleted: { eq: false },
            methodReservationsPlateBarcodes: {
              some: {
                plateBarcode: {
                  eq: nucleofectionToolInfo.sourcePlateInfo[0].plateBarcode,
                },
              },
            },
          },
        },
      });
    });
  };

  const handleWarningDialogClose = async (continueAction: boolean) => {
    setWarningDialogOpen(false);
    if (continueAction && requiredWarnings.length < 1) generateWorklist();
  };

  const handleUploadWorklistClose = async (
    uploadWorklist: boolean,
    worklistRows: UploadedWorklist[]
  ) => {
    setUploadWorklistOpen(false);
    if (!uploadWorklist) return;

    setUploadingWorklist(true);

    try {
      const parsedWorklistInfo = await parseUploadedWorklist(
        worklistRows,
        fetchNucPlateInfo
      );

      nucleofectionDispatch({
        type: NucleofectionInternalAction.SET_STATE,
        payload: {
          key: "uploadedWorklist",
          value: true,
        },
      });

      nucleofectionDispatch({
        type: NucleofectionInternalAction.SET_STATE,
        payload: { key: "uploadedWorklist", value: true },
      });

      nucleofectionDispatch({
        type: NucleofectionInternalAction.SET_STATE,
        payload: { key: "uploadedNucleofectionPlate", value: true },
      });

      dispatch(
        NucleofectionToolActions.UPLOAD_SOURCE_CPD_NUC_INT_DEST_PLATE_INFO({
          sourcePlateInfo: parsedWorklistInfo.sourcePlateInfo,
          compoundPlateInfo: parsedWorklistInfo.compoundPlateInfo,
          nucPlateInfo: parsedWorklistInfo.nucPlateInfo,
          intPlateInfo: parsedWorklistInfo.intPlateInfo,
          destPlateInfo: parsedWorklistInfo.destPlateInfo,
        })
      );

      dispatch(
        NucleofectionToolActions.UPLOAD_WORKLIST_VALUES({
          sourceToNuc: parsedWorklistInfo.worklistValues.sourceToNuc,
          nucToInt: parsedWorklistInfo.worklistValues.nucToInt,
          intToDest: parsedWorklistInfo.worklistValues.intToDest,
        })
      );

      if (parsedWorklistInfo.intPlateInfo[0]?.plateBarcode) {
        dispatch(
          NucleofectionToolActions.UPDATE_INT_PLATEBARCODE({
            intPlateBarcode: parsedWorklistInfo.intPlateInfo[0].plateBarcode,
          })
        );
      }

      nucleofectionDispatch({
        type: NucleofectionInternalAction.UPDATE_METHOD_SETTINGS_FROM_UPLOAD,
        payload: { methodSettings: parsedWorklistInfo.methodSettings },
      });
    } catch (err: any) {
      Alert({
        type: AlertType.ERROR,
        message:
          err?.message ??
          "Failed to upload worklist. Please check the file and try again.",
      });
      return;
    } finally {
      setUploadingWorklist(false);
    }
  };

  return (
    <React.Fragment>
      <StyledToolbarSection>
        <Tooltip
          onOpen={checkForWarnings}
          title={
            isSourcePlateAlreadyReserved ? (
              "Source plate has already reserved. Delete existing reservation if you'd like to use it."
            ) : (
              <WorklistToolWarnings
                requiredWarnings={requiredWarnings}
                optionalWarnings={optionalWarnings}
              />
            )
          }
          placement="right"
        >
          <div>
            <StyledIconButton
              onClick={handleGenerateWorklistClick}
              disabled={isSourcePlateAlreadyReserved}
            >
              <GetAppTwoToneIcon />
            </StyledIconButton>
          </div>
        </Tooltip>
        <StyledIconButton onClick={() => setUploadWorklistOpen(true)}>
          <Tooltip title="Upload Worklist" placement="right">
            <UploadOutlinedIcon />
          </Tooltip>
        </StyledIconButton>

        <StyledIconButton
          disabled={
            methodReservation?.existingMethodReservation
              ? !(methodReservation?.existingMethodReservation?.length > 0)
              : true
          }
          onClick={() => handleDeleteMethodReservationClick()}
        >
          <Tooltip title="Delete Existing Reservation" placement="right">
            <DeleteForeverIcon />
          </Tooltip>
        </StyledIconButton>
      </StyledToolbarSection>
      <WarningDialog
        title={"Warning"}
        message={optionalWarnings}
        isOpen={warningDialogOpen}
        handleClose={(continueAction) =>
          handleWarningDialogClose(continueAction)
        }
      />
      <UploadWorklist
        isOpen={uploadWorklistOpen}
        handleClose={(uploadWorklist, worklistRows) =>
          handleUploadWorklistClose(uploadWorklist, worklistRows)
        }
      />
    </React.Fragment>
  );
};
